import type { ReactNode } from 'react';

// CLIENT PORTAL RESTYLE — the small shared controls.
//
// ⚠️⚠️ THE SELECTED CHIP CARRIES KYCK'S COLOURS, NOT THE CLIENT'S, and it
// carries the state with WEIGHT as well as background so a palette change can
// never make the affordance invisible. Text on kelly is ALWAYS ink (6.18:1);
// white on kelly is 2.84:1 and fails.
//
// ⚠️⚠️ `rpt-switch-on` AND ITS ABSENCE ARE MUTUALLY EXCLUSIVE SELECTORS in the
// stylesheet, so specificity never arbitrates between the two states. The
// earlier version lost that fight silently: the base rule was (0,2,1) and the
// selected rule (0,2,0), so every pressed chip repainted as unpressed while
// aria-pressed stayed correct — invisible to a unit test, and to a browser
// check that only asked whether the CHART changed.

export interface SwitchOption<T extends string> {
  value: T;
  label: string;
}

export function SwitchGroup<T extends string>({
  ariaLabel,
  value,
  onChange,
  options,
}: {
  ariaLabel: string;
  value: T;
  onChange: (next: T) => void;
  options: ReadonlyArray<SwitchOption<T>>;
}) {
  return (
    <div className="rpt-switch" role="group" aria-label={ariaLabel}>
      {options.map((o) => {
        const on = o.value === value;
        return (
          <button
            key={o.value}
            type="button"
            aria-pressed={on}
            className={on ? 'rpt-switch-on' : undefined}
            onClick={() => onChange(o.value)}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

export function Band({
  variant,
  children,
}: {
  variant?: 'hero' | 'contrast' | 'foot';
  children: ReactNode;
}) {
  const cls = variant ? `rpt-band rpt-band--${variant}` : 'rpt-band';
  return (
    <div className={cls}>
      <div className="rpt-shell">{children}</div>
    </div>
  );
}

export function SectionHeading({
  title,
  intro,
  aside,
}: {
  title: string;
  intro?: string;
  aside?: ReactNode;
}) {
  return (
    <div className="rpt-section-head">
      <div>
        <h2 className="rpt-h2 rpt-display">{title}</h2>
        {intro && <p className="rpt-sub rpt-muted">{intro}</p>}
      </div>
      {aside}
    </div>
  );
}

/**
 * A figure that may be unmeasured.
 *
 * ⚠️ An em dash, never a zero — and the class is what carries the emphasis, so
 * a dash never looks like a styled figure.
 */
export function Figure({ value, className }: { value: string | null; className?: string }) {
  return (
    <span className={`rpt-figure ${className ?? ''}`}>{value ?? '—'}</span>
  );
}
